/**
 * AudioRecorder Component
 *
 * Browser-based audio recording using MediaRecorder API.
 * Handles microphone permissions, recording state, and playback preview.
 */

import { useState, useRef, useCallback, useEffect } from 'react';

export interface AudioRecorderProps {
  onRecordingComplete: (blob: Blob, mimeType: string) => void;
  onRecordingClear: () => void;
  disabled?: boolean;
}

type RecordingState = 'idle' | 'requesting' | 'recording' | 'recorded' | 'error';

interface RecorderError {
  type: 'permission' | 'unsupported' | 'recording';
  message: string;
}

/**
 * Determines the best supported audio MIME type for the browser.
 * Returns null if MediaRecorder API is not available.
 */
function getSupportedMimeType(): string | null {
  // Guard against browsers without MediaRecorder support
  if (typeof MediaRecorder === 'undefined') {
    return null;
  }

  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4',
  ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return null;
}

/**
 * Formats elapsed time in MM:SS format.
 */
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

export function AudioRecorder({
  onRecordingComplete,
  onRecordingClear,
  disabled = false,
}: AudioRecorderProps) {
  const [state, setState] = useState<RecordingState>('idle');
  const [error, setError] = useState<RecorderError | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);
  const mimeTypeRef = useRef<string>('audio/webm');

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const startRecording = useCallback(async () => {
    // Check MediaRecorder support
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError({
        type: 'unsupported',
        message: 'Audio recording not supported in this browser.',
      });
      setState('error');
      return;
    }

    const mimeType = getSupportedMimeType();
    if (!mimeType) {
      setError({
        type: 'unsupported',
        message: 'Audio recording not supported in this browser.',
      });
      setState('error');
      return;
    }

    setState('requesting');
    setError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      mimeTypeRef.current = mimeType;

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setState('recorded');
        onRecordingComplete(blob, mimeType);

        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.onerror = () => {
        setError({
          type: 'recording',
          message: 'Recording interrupted. Please try again.',
        });
        setState('error');
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.start();
      setState('recording');
      setElapsedTime(0);

      // Start timer
      timerRef.current = window.setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError({
          type: 'permission',
          message: 'Microphone access required. Please enable in browser settings.',
        });
      } else {
        setError({
          type: 'recording',
          message: 'Failed to start recording. Please try again.',
        });
      }
      setState('error');
    }
  }, [onRecordingComplete]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const clearRecording = useCallback(() => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setState('idle');
    setElapsedTime(0);
    setError(null);
    chunksRef.current = [];
    onRecordingClear();
  }, [audioUrl, onRecordingClear]);

  return (
    <div className="card recorder-card">
      <h3 className="mb-4">Audio Recording</h3>

      {/* Error display */}
      {error && (
        <div className="error-box mb-4">
          {error.message}
        </div>
      )}

      {/* Recording controls */}
      <div className="recorder-controls">
        {state === 'idle' && (
          <button
            className="btn btn-primary"
            onClick={startRecording}
            disabled={disabled}
          >
            Start Recording
          </button>
        )}

        {state === 'requesting' && (
          <span className="text-muted">Requesting microphone access...</span>
        )}

        {state === 'recording' && (
          <>
            <button className="btn btn-secondary" onClick={stopRecording}>
              Stop Recording
            </button>
            <div className="recording-indicator">
              <span className="recording-dot" />
              <span className="mono">{formatTime(elapsedTime)}</span>
            </div>
          </>
        )}

        {state === 'recorded' && (
          <button
            className="btn btn-secondary"
            onClick={clearRecording}
            disabled={disabled}
          >
            Re-record
          </button>
        )}

        {state === 'error' && (
          <button className="btn btn-primary" onClick={startRecording}>
            Try Again
          </button>
        )}
      </div>

      {/* Audio preview */}
      {audioUrl && state === 'recorded' && (
        <div className="audio-preview">
          <label className="label">Preview</label>
          <audio
            controls
            src={audioUrl}
            className="audio-player"
          />
          <div className="audio-duration">
            Duration: {formatTime(elapsedTime)}
          </div>
        </div>
      )}
    </div>
  );
}
